import React, { useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import Sidebar from './Sidebar'
import NotificationsIcon from '@mui/icons-material/Notifications'
import HelpIcon from '@mui/icons-material/Help'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ExecutionOverview from './ExecutionStats/ExecutionOverview'
import RunLog from './ExecutionStats/RunLog'
import { keepingNavState } from '../utils/searchParamsNavigation'
import { dsVar } from '../theme/tokens';

/**
 * Overview is the run statistics, counted by the control plane from the
 * execution logs. Run log is every run behind those counts, newest first, with
 * each run's steps a click away. Both read the same execution-stats API.
 */
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'runs', label: 'Run log' }
]

const MonitoringDashboard = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'runs' ? 'runs' : 'overview'
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Only what both tabs mean the same way carries across; each tab's own
  // filters (and an open run) would be meaningless, or wrong, on the other.
  const SHARED_PARAMS = ['range', 'versions']

  const selectTab = (key) => {
    if (key === tab) return
    const next = new URLSearchParams()
    SHARED_PARAMS.forEach((name) => {
      const value = searchParams.get(name)
      if (value) next.set(name, value)
    })
    if (key !== 'overview') next.set('tab', key)
    setDrawerOpen(false)
    setSearchParams(next, keepingNavState(location.state, { replace: true }))
  }

  return (
    <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
      <Sidebar activePage="activity-monitor" forceCollapsed={drawerOpen} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden bg-page">
        {/* Global Header */}
        <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
          <div className="flex-1" />
          <div className="flex items-center gap-4 ml-8">
            <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
              <NotificationsIcon />
            </button>
            <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
              <HelpIcon />
            </button>
            <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
              <AccountCircleIcon />
            </button>
          </div>
        </header>

        {/* Title + tabs */}
        <div className="flex items-center justify-between px-8 pt-6 pb-2 shrink-0 text-left">
          <h2 className="text-2xl m-0 p-0 font-bold" style={{ color: dsVar('accent-fg') }}>Activity Monitor</h2>
          <div className="bg-surface p-1 rounded-full border border-line flex gap-1" role="tablist">
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                onClick={() => selectTab(key)}
                className={`px-6 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all border-none cursor-pointer ${tab === key
                  ? 'bg-accent text-on-accent'
                  : 'text-fg-2 hover:text-fg-body bg-transparent'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Content */}
        {tab === 'overview' ? (
          <ExecutionOverview onDrawerChange={setDrawerOpen} />
        ) : (
          <RunLog onDrawerChange={setDrawerOpen} />
        )}
      </main>
    </div>
  )
}

export default MonitoringDashboard